import React, { useState, useEffect } from "react";
import {
    Container,
    Paper,
    Typography,
    Button,
    CircularProgress,
    Box,
    TextField,
    Autocomplete, Backdrop,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { toast, ToastContainer } from "react-toastify";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import baseURL from "../../config";
import Navbar from "../../Navbar/Navbar";

const VesselOwnerContract = () => {
    const [document, setDocument] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [vesselOwners, setVesselOwners] = useState([]);
    const [vesselData, setVesselData] = useState([]);
    const navigate = useNavigate();

    // ✅ Fetch Vessel Owner Data from API
    useEffect(() => {
        const fetchVesselOwners = async () => {
            try {
                const token = localStorage.getItem("token");
                const response = await axios.get(`${baseURL}/api/getvesselownerforcontract`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                setVesselData(response.data.data);
                // console.log(response.data.data);
            } catch (error) {
                console.error("Error fetching vessel owners:", error);
                toast.error("Failed to fetch Vessel Owners.");
            }
        };

        fetchVesselOwners();
    }, []);

    // ✅ Handle File Change
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setDocument(file);
            
        }
    };

    // ✅ Remove File
    const handleRemoveFile = () => {
        setDocument(null);
        toast.info("Document removed");
    };

    // ✅ Handle Vessel Owner Change (Multi-Select)
    const handleVesselOwnerChange = (event, value) => {
        setVesselOwners(value);
    };

    // ✅ Handle Form Submit
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!document) {
            toast.error("Please upload a document.");
            return;
        }

        if (vesselOwners.length === 0) {
            toast.error("Please select at least one Vessel Owner.");
            return;
        }

        setIsLoading(true);

        // ✅ Creating the payload in the desired format
        const vesselOwnersData = vesselOwners.map((owner) => ({
            vesselOwnerId: owner._id,
            company_shortname: owner.company_shortname,
        }));

        const formData = new FormData();
        formData.append("file", document);
        formData.append("vesselOwners", JSON.stringify(vesselOwnersData));

        try {
            const token = localStorage.getItem("token");
            await axios.post(`${baseURL}/api/vesselOwnercontract`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                    Authorization: `Bearer ${token}`,
                },
            });

            toast.success("Contract uploaded successfully!");
            setTimeout(() => {
                navigate("/vessel_owner_table");
              }, 1000); 
        } catch (error) {
            console.error("Error uploading document:", error);
            toast.error("Failed to upload document.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Navbar />
            {isLoading && (
                <Backdrop
                    sx={{
                        color: "#fff",
                        zIndex: (theme) => theme.zIndex.drawer + 1,
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        backdropFilter: "blur(5px)",
                        display: "flex",
                        flexDirection: "column",
                    }}
                    open={isLoading}
                >
                    <CircularProgress color="inherit" size={60} />
                    <Typography
                        variant="h6"
                        color="inherit"
                        sx={{ marginTop: 2 }}
                    >
                        Submitting your contract...
                    </Typography>
                </Backdrop>
            )}
            <Container maxWidth="sm" sx={{ mt: 4, mb: 8 }}>
                <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
                    <Typography variant="h4" gutterBottom align="center">
                        Upload Vessel Owner Contract
                    </Typography>

                    <form onSubmit={handleSubmit}>
                        {/* ✅ File Upload Section First */}
                        <Box sx={{ mb: 3 }}>
                            {document ? (
                                <Box>
                                    <Typography variant="body1" sx={{ mb: 1 }}>
                                        File: {document.name}
                                    </Typography>
                                    <Box sx={{ display: "flex", gap: 2 }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                            component="label"
                                        >
                                            Replace File
                                            <input type="file" hidden onChange={handleFileChange} />
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="error"
                                            onClick={handleRemoveFile}
                                        >
                                            Remove
                                        </Button>
                                    </Box>
                                </Box>
                            ) : (
                                <Button
                                    variant="contained"
                                    component="label"
                                    fullWidth
                                    sx={{ py: 1.5 }}
                                >
                                    <CloudUploadIcon sx={{ mr: 1 }} />
                                    Upload Document
                                    <input type="file" hidden onChange={handleFileChange} />
                                </Button>
                            )}
                        </Box>

                        {/* ✅ Multi-Select Vessel Owner */}
                        <Autocomplete
                            multiple
                            options={vesselData}
                            getOptionLabel={(option) => option.company_shortname || ""}
                            isOptionEqualToValue={(option, value) => option._id === value._id}
                            onChange={handleVesselOwnerChange}
                            value={vesselOwners}
                            filterSelectedOptions
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Vessel Owner(s)"
                                    fullWidth
                                    sx={{ mb: 3 }}
                                    error={vesselOwners.length === 0}
                                    helperText={vesselOwners.length === 0 ? "Please select at least one Vessel Owner." : ""}
                                />
                            )}
                        />



                        {/* ✅ Submit Button */}
                        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                            <Button
                                type="submit"
                                variant="contained"
                                color="success"
                                disabled={isLoading || !document || vesselOwners.length === 0}
                                size="large"
                                sx={{ minWidth: 150 }}
                            >
                                {isLoading ? <CircularProgress size={24} /> : "Submit Document"}
                            </Button>
                        </Box>
                    </form>
                </Paper>
            </Container>

             <ToastContainer />
        </>
    );
};

export default VesselOwnerContract;
